

























































































































import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';
import {
  Button,
  Checkbox,
  Input,
  Option,
  OptionGroup,
  Select,
  Tooltip
} from 'element-ui';
import PortFilter from '@/components/inputs/PortFilter.vue';
import { cargoOptions } from '@/constants/cargoOptions';
import { tradeDirectionOptions } from '@/constants/tradeDirectionOptions';

import customerOrganizations from '@/mixins/customerOrganizationOptions';

// types
import { SelectOption, ShipmentListOrderEnum } from '@/types/types';
import {
  CargoFilterEnum,
  CustomerOrganizationFields,
  OperatorFields,
  ShipmentOrderEnum,
  TransportationTypeEnum
} from '@/types/graphql/schemaTypes';

import CopyShipment from '../CopyShipment.vue';

// gql
import filterListQuery from '@/graphql/queries/operator/FilterList.gql';

const OPERATOR_FILTER_SHIPMENT_STORAGE = 'operator_filter_shipment';
const OPERATOR_FILTER_INQUIRY_STORAGE = 'operator_filter_inquiry';
const DEFAULT_OPERATOR_FILTER = 'all';
const DEFAULT_ORDER = ShipmentListOrderEnum.updated_at;

@Component({
  apollo: {
    operatorOptions: {
      query: filterListQuery,
      prefetch: true,
      fetchPolicy: 'cache-and-network',
      update(data) {
        return this.formatOperatorFilters(data.operators);
      }
    }
  },
  components: {
    PortFilter,
    'el-button': Button,
    'el-checkbox': Checkbox,
    'el-input': Input,
    'el-option': Option,
    'el-option-group': OptionGroup,
    'el-select': Select,
    'el-tooltip': Tooltip
  }
})
export default class Filters extends Mixins(customerOrganizations) {
  @Prop({ default: 'shipment' }) private type!: 'shipment' | 'inquiry';

  private customerOrganizationsOptions: SelectOption<string>[] = [];
  private readonly cargoOptions = cargoOptions();
  private readonly shipmentOrderEnum = ShipmentOrderEnum;
  private readonly size = 'mini';
  private readonly tradeDirectionOptions = tradeDirectionOptions();
  private readonly defaultOperator = DEFAULT_OPERATOR_FILTER;
  private readonly defaultOrder = DEFAULT_ORDER; // updated_at

  // filters
  private billingIncomplete = null;
  private cargo: string | null = null;
  private customerOrganizationsFilter: string | null = null;
  private operatorFilter: string | null = this.defaultOperator;
  private orderBy = this.defaultOrder; // updated_at
  private pod: string | null = null; // delivery
  private pol: string | null = null; // loading
  private priority: number | null = null;
  private reference = '';
  private status = 'all';
  private tradeDirection: string | null = null;

  private created() {
    this.setFilters();
    this.formatOperatorFilters([]);
    this.$emit('ready');
  }

  private beforeDestroy() {
    this.setOperatorFilter();
  }

  private clearFilters() {
    this.billingIncomplete = null;
    this.cargo = null;
    this.customerOrganizationsFilter = null;
    this.operatorFilter = this.defaultOperator;
    this.orderBy = this.defaultOrder; // updated_at;
    this.pod = null; // delivery
    this.pol = null; // loading
    this.priority = null;
    this.reference = '';
    this.status = 'all';
    this.tradeDirection = null;
  }

  private clearBillingIncomplete(statusOption) {
    if (statusOption !== 'shipment_completed') {
      this.billingIncomplete = null;
    }
  }

  private formatOperatorFilters(operators: OperatorFields[]) {
    const searchedOperators = operators.map((operator: OperatorFields) => {
      return {
        label: operator.name || '',
        value: operator.id
      };
    }).filter((op) => {
      return op.value !== this.$store.state.User.profile?.id;
    });

    return [
      {
        label: '',
        options: [
          {
            value: this.defaultOperator,
            label: this.$t('inquiry.filter.all-operators') as string
          },
          { value: 'mine', label: this.$t('inquiry.filter.mine') as string },
          { value: 'unassigned', label: this.$t('inquiry.filter.unassigned') as string }
        ]
      },
      {
        label: this.$t('inquiry.filter.operators') as string,
        options: searchedOperators
      }
    ];
  }

  private priorityOptions() {
    return [
      {
        label: this.$t('inquiry.filter.priority.high'),
        value: 1
      },
      {
        label: this.$t('inquiry.filter.priority.medium'),
        value: 2
      },
      {
        label: this.$t('inquiry.filter.priority.low'),
        value: 3
      }
    ];
  }

  private shipmentOrderOptions() {
    return [
      {
        label: this.$t('shipmentOrder.updated_at'),
        value: ShipmentListOrderEnum.updated_at
      },
      {
        label: this.$t('shipmentOrder.import_arrival_export_delivery_asc'),
        value: ShipmentListOrderEnum.import_arrival_export_delivery_asc
      },
      {
        label: this.$t('shipmentOrder.import_delivery_export_collection_asc'),
        value: ShipmentListOrderEnum.import_delivery_export_collection_asc
      },
      {
        label: this.$t('shipmentOrder.customs_clearance_asc'),
        value: ShipmentListOrderEnum.customs_clearance_asc
      }
    ];
  }

  private statusOptions() {
    if (this.type === 'inquiry') {
      return [
        {
          label: this.$t('inquiry.filter.all'),
          value: 'all'
        },
        {
          label: this.$t('inquiry.filter.inquiry_quoted'),
          value: 'inquiry_quoted'
        },
        {
          label: this.$t('inquiry.filter.inquiry_created'),
          value: 'inquiry_created'
        },
        {
          label: this.$t('inquiry.filter.inquiry_cancelled'),
          value: 'inquiry_cancelled'
        }
      ];
    } // shipment
    return [
      {
        label: this.$t('shipment.filter.in-progress'),
        value: 'all'
      },
      {
        label: this.$t('shipment.filter.arrived'),
        value: 'shipment_completed'
      },
      {
        label: this.$t('shipment.filter.cancelled'),
        value: 'shipment_cancelled'
      }
    ];
  }

  // watch fullpath to reset filter if need
  @Watch('$route.fullPath')
  private setFilters() {
    const query = this.$route.query;

    this.billingIncomplete = query.billingIncomplete === 'true' || null;
    this.cargo = (query.cargo as string) || null;
    this.customerOrganizationsFilter = (query.customerOrganizationId as string) || null;
    this.operatorFilter = (query.operatorId as string) || this.getOperatorFilter();
    this.orderBy = ShipmentListOrderEnum[query.orderBy as string] || this.defaultOrder; // updated_at
    this.pod = (query.pod as string) || null; // delivery
    this.pol = (query.pol as string) || null; // loading
    this.reference = (query.reference as string) || '';
    this.status = (query.status as string) || 'all';
    this.tradeDirection = (query.tradeDirection as string) || null;
    this.priority = null;

    if (this.type === 'inquiry' && query.priority) {
      this.priority = parseInt((query.priority as string), 10);
    }
  }

  get hasAppliedFilters() {
    return !!this.billingIncomplete ||
           !!this.cargo ||
           !!this.customerOrganizationsFilter ||
           this.operatorFilter !== this.defaultOperator ||
           this.orderBy !== this.defaultOrder || // updated_at
           !!this.pod ||
           !!this.pol ||
           this.status !== 'all' ||
           !!this.tradeDirection ||
           !!this.priority;
  }

  get transportationType() {
    switch (this.cargo) {
      case CargoFilterEnum.LCL:
      case CargoFilterEnum.FCL:
        return TransportationTypeEnum.sea;
      case CargoFilterEnum.AIR:
        return TransportationTypeEnum.air;
      default:
        return TransportationTypeEnum.any;
    }
  }

  // Operator Filter Storage
  private setOperatorFilter(): void {
    const storageName = this.type === 'inquiry'
      ? OPERATOR_FILTER_INQUIRY_STORAGE : OPERATOR_FILTER_SHIPMENT_STORAGE;
    sessionStorage.setItem(storageName, this.operatorFilter);
  }

  /**
   * Operator Filter Storage
   *
   * @returns stored value or DEFAULT_OPERATOR_FILTER if the value is null
   */
  private getOperatorFilter(): string {
    const storageName = this.type === 'inquiry'
      ? OPERATOR_FILTER_INQUIRY_STORAGE : OPERATOR_FILTER_SHIPMENT_STORAGE;
    return sessionStorage.getItem(storageName) || this.defaultOperator;
  }

  @Watch('billingIncomplete')
  @Watch('cargo')
  @Watch('customerOrganizationsFilter')
  @Watch('operatorFilter')
  @Watch('orderBy')
  @Watch('pod')
  @Watch('pol')
  @Watch('priority')
  @Watch('reference')
  @Watch('status')
  @Watch('tradeDirection')
  replaceUrl() {
    const queryPart = {
      billingIncomplete: this.billingIncomplete ? 'true' : null,
      cargo: this.cargo,
      customerOrganizationId: this.customerOrganizationsFilter,
      operatorId: this.operatorFilter,
      orderBy: this.orderBy, // updated_at
      pod: this.pod, // delivery
      pol: this.pol, // loading
      priority: this.priority,
      reference: this.reference,
      status: this.status === 'all' ? null : this.status,
      tradeDirection: this.tradeDirection
    };
    if (this.isQueryDifferent(queryPart, this.$route.query)) {
      // @ts-ignore
      this.$router.replace({
        path: this.$route.path,
        query: {
          ...queryPart,
          // pagination
          page: 1,
          size: this.$route.query.size
        }
      });
    }
  }

  private isQueryDifferent(query1: object, query2: object) {
    let different = false;
    Object.keys(query1).forEach(key => {
      if (query1[key] !== query2[key]) {
        different = true;
      }
    });
    return different;
  }
}
